import React from 'react';
import { useTranslation } from 'react-i18next';
import { Divider, Steps, Typography } from 'antd';
import { FileExclamationOutlined } from '@ant-design/icons';
import type { CargoRes } from 'api/swagger';
import { isFullyCompleted, isFullyLoaded } from 'shared/utils/statuses.ts';
import { isExpiredDate } from 'shared/utils/time/is-expired-period-of-date.ts';
import { CardUi } from 'components/common/card-ui';
import styles from './cargo-addresses-sidebar-card.module.scss';
import { CargoAddressBox } from './componets/cargo-address-box';

interface ICargoAddressesSidebarCardProps {
  cargo: CargoRes;
}

export const CargoAddressesSidebarCard: React.FC<ICargoAddressesSidebarCardProps> = (props) => {
  const { cargo } = props;
  const { t } = useTranslation();

  const loadingTimePeriod = !isFullyLoaded(cargo.statuses)
    ? isExpiredDate(cargo.loadingDateFrom, cargo.loadingDateTo)
    : 'ok';

  const unloadingTimePeriod = !isFullyCompleted(cargo.statuses)
    ? isExpiredDate(cargo.unloadingDateFrom, cargo.unloadingDateTo)
    : 'ok';

  return (
    <CardUi>
      <Steps
        direction='vertical'
        size='small'
        items={[
          {
            title: (
              <>
                <Typography.Text className={styles.title}>{t('cargo:loading')}</Typography.Text>
                <div>
                  {loadingTimePeriod === 'soon' ? (
                    <Typography.Text className={styles.soon}>Soon</Typography.Text>
                  ) : null}

                  {loadingTimePeriod === 'between' ? (
                    <Typography.Text className={styles.warning}>Need action</Typography.Text>
                  ) : null}
                  {loadingTimePeriod === 'expired' ? (
                    <Typography.Text className={styles.expired}>Overdue</Typography.Text>
                  ) : null}
                </div>
                <CargoAddressBox
                  address={cargo.loadingAddress}
                  customAddress={cargo.loadingCustomAddress}
                  customRequirements={cargo.loadingCustom}
                  dateFrom={cargo.loadingDateFrom}
                  dateTo={cargo.loadingDateTo}
                  actualDate={cargo.pickupDate}
                />
              </>
            ),
            status: 'finish',
          },
          {
            title: (
              <div>
                <Typography.Text className={styles.title}>{t('cargo:unloading')}</Typography.Text>
                <div>
                  {unloadingTimePeriod === 'soon' ? (
                    <Typography.Text className={styles.soon}>Soon</Typography.Text>
                  ) : null}

                  {unloadingTimePeriod === 'between' ? (
                    <Typography.Text className={styles.warning}>Need action</Typography.Text>
                  ) : null}
                  {unloadingTimePeriod === 'expired' ? (
                    <Typography.Text className={styles.expired}>Overdue</Typography.Text>
                  ) : null}
                </div>

                <CargoAddressBox
                  address={cargo.unloadingAddress}
                  customAddress={cargo.unloadingCustomAddress}
                  customRequirements={cargo.unloadingCustom}
                  dateFrom={cargo.unloadingDateFrom}
                  dateTo={cargo.unloadingDateTo}
                  actualDate={cargo.deliveryDate}
                />
                {!cargo.cmrReceived && isFullyCompleted(cargo.statuses) ? (
                  <>
                    <Divider className={styles.divider} />
                    <Typography.Text className={styles.cmrNotReceived}>
                      <FileExclamationOutlined />
                      {t('cargo:cmr_not_received')}
                    </Typography.Text>
                  </>
                ) : null}
              </div>
            ),
            status: 'finish',
          },
          ...(cargo.assetReturnAddress
            ? [
                {
                  title: (
                    <div>
                      <Typography.Text className={styles.title}>{t('cargo:return_address')}</Typography.Text>
                      <CargoAddressBox address={cargo.assetReturnAddress} />
                    </div>
                  ),
                  status: 'finish' as const,
                },
              ]
            : []),
        ]}
      />
    </CardUi>
  );
};
